/**
 * Matching Bottom Finder Module
 * Shown on Tops product pages. Lets shoppers pick a pant size and
 * returns in-stock Bottoms products in the currently selected top color.
 */

class MatchingBottomFinder {
  constructor(containerSelector, dataScriptSelector) {
    this.container = document.querySelector(containerSelector);
    if (!this.container) return;

    const dataScript = document.querySelector(dataScriptSelector);
    if (!dataScript) return;

    try {
      this.bottoms = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('Error parsing matching bottoms data:', e);
      this.bottoms = [];
    }

    this.pantSizes = ['1', '2', '4', '6', '8', '10', '12', '14', '16'];

    this.heightResetTimer = null;
    this.scrollPinTimer = null;

    this.toggleButton = null;
    this.toggleContent = null;
    this.pantSizeDropdown = null;
    this.resultsContainer = null;

    this.init();
  }

  init() {
    this.render();
    this.setupEventListeners();
  }

  render() {
    const optionsHtml = this.pantSizes
      .map(size => `<option value="${size}">${size}</option>`)
      .join('');

    const html = `
      <div class="matching-bottom-finder">
        <button class="matching-bottom-finder__toggle" type="button">
          <span>FIND A MATCHING BOTTOM</span>
          <span class="matching-bottom-finder__toggle-icon">▼</span>
        </button>
        <div class="matching-bottom-finder__content">
          <div class="matching-bottom-finder__content-inner">
            <div class="matching-finder-group">
              <label for="matching-pant-size">PANT SIZE</label>
              <div class="matching-finder-inline">
                <div class="matching-finder-select-wrap">
                  <select id="matching-pant-size" class="matching-pant-size-dropdown">
                    <option value="">—</option>
                    ${optionsHtml}
                  </select>
                </div>
                <p class="matching-finder-empty"></p>
              </div>
            </div>
            <div class="matching-finder-results"></div>
          </div>
        </div>
      </div>
    `;

    this.container.innerHTML = html;

    this.toggleButton = this.container.querySelector('.matching-bottom-finder__toggle');
    this.toggleContent = this.container.querySelector('.matching-bottom-finder__content');
    this.pantSizeDropdown = this.container.querySelector('.matching-pant-size-dropdown');
    this.emptyMessage = this.container.querySelector('.matching-finder-empty');
    this.resultsContainer = this.container.querySelector('.matching-finder-results');
  }

  setupEventListeners() {
    this.toggleButton.addEventListener('click', () => this.toggleCollapsible());
    this.pantSizeDropdown.addEventListener('change', () => this.handlePantSizeChange());

    // Re-filter automatically if the shopper changes the top's color after picking a pant size
    document.querySelectorAll('input[type="radio"][data-index="option1"]').forEach(input => {
      input.addEventListener('change', () => {
        if (this.pantSizeDropdown.value) this.handlePantSizeChange();
      });
    });

    this.resultsContainer.addEventListener('click', (e) => {
      const button = e.target.closest('.matching-finder-add-to-cart');
      if (button) this.handleAddToCart(button);
    });
  }

  toggleCollapsible() {
    const scrollY = window.scrollY;

    this.toggleButton.classList.toggle('is-open');
    this.toggleContent.classList.toggle('is-open');

    // Some browsers try to scroll the toggle/its expanding content into
    // view once the layout changes — pin the scroll position so the page
    // stays put and the revealed content just extends past the viewport.
    window.scrollTo(window.scrollX, scrollY);
    requestAnimationFrame(() => window.scrollTo(window.scrollX, scrollY));
    clearTimeout(this.scrollPinTimer);
    this.scrollPinTimer = setTimeout(() => window.scrollTo(window.scrollX, scrollY), 320);
  }

  getSelectedColor() {
    const colorInput = document.querySelector('input[type="radio"][data-index="option1"]:checked');
    if (colorInput) return colorInput.value.toLowerCase();

    const colorSelect = document.querySelector('.product-form__input--swatch input:checked, .variant-input--color input:checked');
    if (colorSelect) return colorSelect.value.toLowerCase();

    return null;
  }

  handlePantSizeChange() {
    const pantSize = this.pantSizeDropdown.value;

    this.emptyMessage.innerHTML = '';

    if (!pantSize) {
      this.setResultsHtml('');
      return;
    }

    const selectedColor = this.getSelectedColor();

    if (!selectedColor) {
      this.emptyMessage.textContent = 'Select a color above first.';
      this.setResultsHtml('');
      return;
    }

    const matches = this.findMatches(selectedColor, pantSize);

    if (matches.length) {
      this.setResultsHtml(this.buildResultsHtml(matches));
    } else {
      this.showEmpty();
      this.setResultsHtml('');
    }
  }

  // Swaps in new results HTML but animates the container's height from its
  // old size to its new one, instead of snapping straight there — so
  // switching between sizes with different match counts doesn't visually
  // "hop" the rest of the page up or down.
  setResultsHtml(html) {
    const el = this.resultsContainer;
    const oldHeight = el.getBoundingClientRect().height;

    clearTimeout(this.heightResetTimer);
    el.style.transition = '';
    el.style.overflow = 'hidden';
    el.style.height = oldHeight + 'px';

    el.innerHTML = html;

    const newHeight = el.scrollHeight;

    requestAnimationFrame(() => {
      el.style.transition = 'height 0.25s ease';
      el.style.height = newHeight + 'px';
    });

    this.heightResetTimer = setTimeout(() => {
      el.style.transition = '';
      el.style.height = '';
      el.style.overflow = '';
    }, 260);
  }

  findMatches(selectedColor, pantSize) {
    const matches = [];

    this.bottoms.forEach(bottom => {
      const variant = (bottom.variants || []).find(v => {
        return v.available
          && (v.color || '').toLowerCase().trim() === selectedColor
          && (v.size || '').toString().trim() === pantSize;
      });

      if (variant) {
        matches.push({ product: bottom, variant });
      }
    });

    return matches;
  }

  buildResultsHtml(matches) {
    return matches.map(({ product, variant }) => `
      <div class="matching-finder-result-card">
        <a href="${product.url}?variant=${variant.id}">
          <img src="${variant.image}" alt="${this.escapeHtml(product.title)}" loading="lazy">
        </a>
        <a class="matching-finder-result-card__title" href="${product.url}?variant=${variant.id}">${this.escapeHtml(product.title)} • ${this.escapeHtml(variant.size)}</a>
        <p class="matching-finder-result-card__price">${variant.price}</p>
        <button type="button" class="matching-finder-add-to-cart" data-variant-id="${variant.id}" data-size="${this.escapeHtml(variant.size)}">ADD TO CART</button>
      </div>
    `).join('');
  }

  showEmpty() {
    this.emptyMessage.innerHTML = 'No matches in-stock, try a <a href="https://www.maliamills.com/collections/swimwear-bikini-bottoms?filter.v.availability=1">mixer</a>!';
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  handleAddToCart(button) {
    if (button.disabled) return;

    const variantId = button.dataset.variantId;
    const size = button.dataset.size;
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'ADDING...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          // Underscore-prefixed properties are hidden from the cart, checkout,
          // and order confirmation, but still show on the order in admin —
          // this is purely for tracking whether shoppers use this tool.
          properties: {
            '_Added via': 'Matching Bottom Finder',
            '_Matched size': size
          }
        }]
      })
    })
      .then(response => {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then(() => {
        button.textContent = 'ADDED!';
        button.classList.add('is-added');
        setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('is-added');
          button.disabled = false;
        }, 2000);
      })
      .catch(err => {
        console.error('Error adding to cart:', err);
        button.textContent = 'ERROR — TRY AGAIN';
        button.disabled = false;
      });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  new MatchingBottomFinder('.matching-bottom-finder-container', '#MatchingBottomsData');
});
